import { Item } from '../lib/schema';

// Try remote first, fallback to local
const REMOTE_API_URL = 'https://api.horizoonstudio.se';
const LOCAL_API_URL = 'http://172.21.4.130:8055';

class DirectusService {
  private static async getApiUrl(): Promise<string> {
    // Try remote first
    try {
      const response = await fetch(`${REMOTE_API_URL}/server/ping`, {
        method: 'GET',
        timeout: 5000
      } as any);
      if (response.ok) {
        return REMOTE_API_URL;
      }
    } catch (error) {
      console.log('Remote API not available, trying local...');
    }

    // Fallback to local
    try {
      const response = await fetch(`${LOCAL_API_URL}/server/ping`, {
        method: 'GET',
        timeout: 5000
      } as any);
      if (response.ok) {
        return LOCAL_API_URL;
      }
    } catch (error) {
      console.log('Local API not available');
    }

    // Default to local for development
    return LOCAL_API_URL;
  }

  // Get all cards from Directus
  static async getAllCards(): Promise<Item[]> {
    try {
      const apiUrl = await this.getApiUrl();
      const response = await fetch(`${apiUrl}/items/pokemon_cards`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      
      // Transform Directus data to match Item schema
      return data.data.map((card: any) => ({
        id: card.id.toString(),
        name: card.name,
        set: card.set || undefined,
        condition: card.condition || undefined,
        images: card.images || ['', ''],
        qty: card.qty || 1,
        buyPrice: card.buy_price || 0,
        buyDate: card.buy_date || new Date().toISOString().split('T')[0],
        suggestedPrice: card.suggested_price || undefined,
        traderaTitle: card.tradera_title || undefined,
        traderaDesc: card.tradera_desc || undefined,
        sold: card.sold_price ? {
          sellPrice: card.sold_price,
          sellDate: card.sold_date
        } : undefined
      }));
    } catch (error) {
      console.error('Error fetching cards from Directus:', error);
      return [];
    }
  }

  // Add new card to Directus
  static async addCard(card: Omit<Item, 'id'>): Promise<Item> {
    try {
      const apiUrl = await this.getApiUrl();
      const response = await fetch(`${apiUrl}/items/pokemon_cards`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: card.name,
          set: card.set,
          condition: card.condition,
          qty: card.qty,
          buy_price: card.buyPrice,
          buy_date: card.buyDate,
          suggested_price: card.suggestedPrice,
          tradera_title: card.traderaTitle,
          tradera_desc: card.traderaDesc
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Transform Directus response to match Item schema
      return {
        id: data.data.id.toString(),
        name: data.data.name,
        set: data.data.set || undefined,
        condition: data.data.condition || undefined,
        images: data.data.images || ['', ''],
        qty: data.data.qty || 1,
        buyPrice: data.data.buy_price || 0,
        buyDate: data.data.buy_date || new Date().toISOString().split('T')[0],
        suggestedPrice: data.data.suggested_price || undefined,
        traderaTitle: data.data.tradera_title || undefined,
        traderaDesc: data.data.tradera_desc || undefined,
        sold: data.data.sold_price ? {
          sellPrice: data.data.sold_price,
          sellDate: data.data.sold_date
        } : undefined
      };
    } catch (error) {
      console.error('Error adding card to Directus:', error);
      throw error;
    }
  }

  // Update existing card in Directus
  static async updateCard(id: string, updates: Partial<Item>): Promise<Item> {
    try {
      const apiUrl = await this.getApiUrl();
      const response = await fetch(`${apiUrl}/items/pokemon_cards/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: updates.name,
          set: updates.set,
          condition: updates.condition,
          qty: updates.qty,
          buy_price: updates.buyPrice,
          suggested_price: updates.suggestedPrice,
          tradera_title: updates.traderaTitle,
          tradera_desc: updates.traderaDesc,
          sold_price: updates.sold?.sellPrice,
          sold_date: updates.sold?.sellDate
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Transform Directus response to match Item schema
      return {
        id: data.data.id.toString(),
        name: data.data.name,
        set: data.data.set || undefined,
        condition: data.data.condition || undefined,
        images: data.data.images || ['', ''],
        qty: data.data.qty || 1,
        buyPrice: data.data.buy_price || 0,
        buyDate: data.data.buy_date || new Date().toISOString().split('T')[0],
        suggestedPrice: data.data.suggested_price || undefined,
        traderaTitle: data.data.tradera_title || undefined,
        traderaDesc: data.data.tradera_desc || undefined,
        sold: data.data.sold_price ? {
          sellPrice: data.data.sold_price,
          sellDate: data.data.sold_date
        } : undefined
      };
    } catch (error) {
      console.error('Error updating card in Directus:', error);
      throw error;
    }
  }

  // Delete card from Directus
  static async deleteCard(id: string): Promise<void> {
    try {
      const apiUrl = await this.getApiUrl();
      const response = await fetch(`${apiUrl}/items/pokemon_cards/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error deleting card from Directus:', error);
      throw error;
    }
  }

  // Upload images to Directus
  static async uploadImages(files: File[]): Promise<{ files: Array<{ filename: string; path: string }> }> {
    try {
      const apiUrl = await this.getApiUrl();
      const formData = new FormData();
      files.forEach(file => {
        formData.append('file', file);
      });

      const response = await fetch(`${apiUrl}/files`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      return {
        files: data.data.map((file: any) => ({
          filename: file.filename_disk,
          path: `${apiUrl}/assets/${file.filename_disk}`
        }))
      };
    } catch (error) {
      console.error('Error uploading images to Directus:', error);
      throw error;
    }
  }

  // Check Directus connection
  static async checkConnection(): Promise<{ connected: boolean; url: string }> {
    // Try remote first
    try {
      const response = await fetch(`${REMOTE_API_URL}/server/ping`);
      if (response.ok) {
        return { connected: true, url: 'Remote (api.horizoonstudio.se)' };
      }
    } catch (error) {
      console.log('Remote API not available');
    }

    // Try local
    try {
      const response = await fetch(`${LOCAL_API_URL}/server/ping`);
      if (response.ok) {
        return { connected: true, url: 'Local (172.21.4.130:8055)' };
      }
    } catch (error) {
      console.log('Local API not available');
    }

    return { connected: false, url: 'No connection' };
  }
}

export { DirectusService };
